<template>
  <div>
    <div v-if="!readonly" class="cfs-field d-flex flex-column">
      <p class="input-title px-1 pb-1" :class="{ disabled }">
        {{ sharp ? `${label} #` : label }}
      </p>
      <v-combobox
        v-bind="$attrs"
        :placeholder="placeholder || label"
        :label="null"
        dense
        :disabled="disabled"
        :value="selectedItem"
        :item-value="itemValue"
        :item-text="itemText"
        :items="computedItems"
        :clearable="clearable"
        :return-object="true"
        outlined
        v-on="$listeners"
      >
        <template v-for="(_, name) in $scopedSlots" #[name]="slotProps">
          <slot :name="name" v-bind="slotProps"></slot>
        </template>
      </v-combobox>
    </div>
    <CfsReadonlyFieldComponent v-else v-bind="$props" :value="valueStr" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import CfsReadonlyFieldComponent from '@/framework/inputs/special/CfsReadonlyFieldComponent.vue';
import { isObject } from '@/helpers/objects';

type ItemTextType = string | ((item: any) => string);

export default defineComponent({
  name: 'CfsCombobox',
  components: { CfsReadonlyFieldComponent },
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    sharp: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    value: {
      type: [Object, String] as PropType<string | Record<string, unknown>>,
      default: '',
    },
    itemText: {
      type: [String, Function] as PropType<ItemTextType>,
      default: '',
    },
    itemValue: {
      type: String,
      default: '',
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    appendChip: {
      type: [String, Function] as PropType<string | ((item: any) => string)>,
      default: null,
    },
  },
  setup(props) {
    const selectedItem = computed(() => {
      if (!props.value) return null;

      return props.items?.find((item) => {
        if (typeof props.value === 'string') {
          return item[props.itemValue] === props.value;
        }
        return item[props.itemValue] === props.value[props.itemValue];
      });
    });

    const valueStr = computed(() => {
      if (!props.value) return null;

      const item = props.items?.find((item) => {
        if (isObject(props.value)) {
          return (
            item[props.itemValue] === (props.value as Record<string, unknown>)[props.itemValue]
          );
        }
        return item[props.itemValue] === props.value;
      });

      if (!item) return props.value;

      if (typeof props.itemText === 'string') {
        return item[props.itemText];
      }
      if (typeof props.itemText === 'function') {
        return props.itemText(item);
      }
      return item;
    });

    const computedItems = computed(() => {
      const items = props.items.slice();
      try {
        if (typeof props.itemText === 'string') {
          return items.sort((a, b) =>
            String(a?.[props.itemText as string])?.localeCompare(
              String(b?.[props.itemText as string])
            )
          );
        }
        if (typeof props.itemText === 'function') {
          const textFn = props.itemText as (item: any) => string;
          return items.sort((a, b) => textFn(a)?.localeCompare(textFn(b)));
        }
      } catch (e) {
        console.log(e);
      }
      return items;
    });

    return {
      computedItems,
      valueStr,
      selectedItem,
    };
  },
});
</script>
